/**
 * CreateOpdrachtModal Component
 * Admin modal for creating multi-section opdrachten
 */

import React, { useState, useCallback, useEffect } from "react";
import {
  View,
  Text,
  Modal,
  TouchableOpacity,
  TextInput,
  ScrollView,
  Switch,
  Alert,
  ActivityIndicator,
} from "react-native";
import { Ionicons } from "@expo/vector-icons";
import { SafeAreaView } from "react-native-safe-area-context";
import { format } from "date-fns";
import { nl } from "date-fns/locale";
import { useThemeColors } from "@/providers/ThemeProvider";
import { useAuth } from "@/providers/AuthProvider";
import { supabase } from "@/lib/supabase/client";
import { Team } from "@/types";

interface AgendaEventOption {
  id: string;
  title: string;
  starts_at: string;
  is_revealed: boolean;
}
import {
  SectionType,
  OpdrachtConfig,
  ChallengeTargetType,
  ChallengeVisibility,
  SECTION_ICONS,
  SECTION_LABELS,
  SECTION_COLORS,
} from "../types";

interface SectionInput {
  id: string;
  title: string;
  description: string;
  section_type: SectionType;
  points: number;
  is_required: boolean;
  time_limit_minutes: number | null;
  auto_approve: boolean;
}

interface EditChallenge {
  id: string;
  title: string;
  description: string | null;
  target_type: ChallengeTargetType;
  visibility: ChallengeVisibility;
  visible_to_team_id: string | null;
  config: any;
  linked_event_id?: string | null;
  reveal_targets?: string[];
}

interface CreateOpdrachtModalProps {
  visible: boolean;
  onClose: () => void;
  onSuccess: () => void;
  weekendId?: string;
  /** Optional: Pass a challenge to edit instead of create */
  editChallenge?: EditChallenge | null;
}

/**
 * Modal for creating/editing an opdracht with sections
 */
export const CreateOpdrachtModal: React.FC<CreateOpdrachtModalProps> = ({
  visible,
  onClose,
  onSuccess,
  weekendId,
  editChallenge,
}) => {
  const colors = useThemeColors();
  const { activeProfile, activeWeekend } = useAuth();
  const isEditMode = !!editChallenge;

  // Basic info
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [explainer, setExplainer] = useState("");

  // Settings
  const [targetType, setTargetType] = useState<ChallengeTargetType>("team");
  const [visibility, setVisibility] = useState<ChallengeVisibility>("all");
  const [selectedTeamId, setSelectedTeamId] = useState<string | null>(null);

  // Teams list
  const [teams, setTeams] = useState<Team[]>([]);
  const [teamsLoading, setTeamsLoading] = useState(false);

  // Event linking
  const [events, setEvents] = useState<AgendaEventOption[]>([]);
  const [eventsLoading, setEventsLoading] = useState(false);
  const [linkedEventId, setLinkedEventId] = useState<string | null>(null);
  const [revealTargets, setRevealTargets] = useState<string[]>([]);

  const [requireOrder, setRequireOrder] = useState(false);
  const [allowPartialPoints, setAllowPartialPoints] = useState(true);
  const [reviewMode, setReviewMode] = useState<"per_section" | "whole">("whole");

  // Sections
  const [sections, setSections] = useState<SectionInput[]>([]);
  const [showAddSection, setShowAddSection] = useState(false);
  const [editingSectionId, setEditingSectionId] = useState<string | null>(null);

  // Section form (used for both new and edit)
  const [newSectionTitle, setNewSectionTitle] = useState("");
  const [newSectionDescription, setNewSectionDescription] = useState("");
  const [newSectionType, setNewSectionType] = useState<SectionType>("checkbox");
  const [newSectionPoints, setNewSectionPoints] = useState("10");
  const [newSectionRequired, setNewSectionRequired] = useState(true);
  const [newSectionAutoApprove, setNewSectionAutoApprove] = useState(false);

  // State
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [step, setStep] = useState<"info" | "sections" | "settings">("info");

  /**
   * Fetch teams when modal opens
   */
  useEffect(() => {
    const fetchTeams = async () => {
      const weekendToUse = weekendId || activeWeekend?.id;
      if (!weekendToUse) return;

      setTeamsLoading(true);
      try {
        const { data, error } = await supabase
          .from("teams")
          .select("*")
          .eq("weekend_id", weekendToUse)
          .order("name");

        if (error) throw error;
        setTeams(data || []);
      } catch (err) {
        console.error("Error fetching teams:", err);
      } finally {
        setTeamsLoading(false);
      }
    };

    if (visible) {
      fetchTeams();
    }
  }, [visible, weekendId, activeWeekend?.id]);

  /**
   * Fetch agenda events for linking
   */
  useEffect(() => {
    const fetchEvents = async () => {
      const weekendToUse = weekendId || activeWeekend?.id;
      if (!weekendToUse) return;

      setEventsLoading(true);
      try {
        const { data, error } = await supabase
          .from("agenda_events")
          .select("id, title, starts_at, is_revealed")
          .eq("weekend_id", weekendToUse)
          .order("starts_at", { ascending: true });

        if (error) throw error;
        setEvents(data || []);
      } catch (err) {
        console.error("Error fetching events:", err);
      } finally {
        setEventsLoading(false);
      }
    };

    if (visible) {
      fetchEvents();
    }
  }, [visible, weekendId, activeWeekend?.id]);

  /**
   * Load existing challenge data in edit mode
   */
  useEffect(() => {
    const loadEditData = async () => {
      if (!visible || !editChallenge) return;

      // Load basic info
      setTitle(editChallenge.title);
      setDescription(editChallenge.description || "");
      setTargetType(editChallenge.target_type);
      setVisibility(editChallenge.visibility);
      setSelectedTeamId(editChallenge.visible_to_team_id);

      // Load config
      const config = editChallenge.config || {};
      setExplainer(config.explainer || "");
      setRequireOrder(config.require_order || false);
      setAllowPartialPoints(config.allow_partial_points ?? true);
      setReviewMode(config.review_mode || "whole");

      // Load event linking
      setLinkedEventId(editChallenge.linked_event_id || null);
      setRevealTargets(editChallenge.reveal_targets || []);

      // Load sections
      try {
        const { data: sectionsData, error } = await supabase
          .from("challenge_sections")
          .select("*")
          .eq("challenge_id", editChallenge.id)
          .order("order_index", { ascending: true });

        if (error) throw error;

        const loadedSections: SectionInput[] = (sectionsData || []).map((s: any) => ({
          id: s.id,
          title: s.title,
          description: s.description || "",
          section_type: s.section_type,
          points: s.points || 0,
          is_required: s.is_required ?? true,
          time_limit_minutes: s.time_limit_minutes,
          auto_approve: s.config?.auto_approve || false,
        }));

        setSections(loadedSections);
      } catch (err) {
        console.error("Error loading sections:", err);
      }
    };

    loadEditData();
  }, [visible, editChallenge]);

  /**
   * Calculate total points
   */
  const totalPoints = sections.reduce((sum, s) => sum + s.points, 0);

  /**
   * Add a new section
   */
  const handleAddSection = () => {
    if (!newSectionTitle.trim()) {
      Alert.alert("Fout", "Vul een titel in voor de sectie");
      return;
    }

    const newSection: SectionInput = {
      id: `temp-${Date.now()}`,
      title: newSectionTitle.trim(),
      description: newSectionDescription.trim(),
      section_type: newSectionType,
      points: parseInt(newSectionPoints) || 0,
      is_required: newSectionRequired,
      time_limit_minutes: null,
      auto_approve: newSectionAutoApprove,
    };

    setSections([...sections, newSection]);

    // Reset form
    setNewSectionTitle("");
    setNewSectionDescription("");
    setNewSectionType("checkbox");
    setNewSectionPoints("10");
    setNewSectionRequired(true);
    setNewSectionAutoApprove(false);
    setShowAddSection(false);
  };

  /**
   * Remove a section
   */
  const handleRemoveSection = (sectionId: string) => {
    setSections(sections.filter((s) => s.id !== sectionId));
  };

  /**
   * Start editing a section
   */
  const handleStartEditSection = (section: SectionInput) => {
    setEditingSectionId(section.id);
    setNewSectionTitle(section.title);
    setNewSectionDescription(section.description);
    setNewSectionType(section.section_type);
    setNewSectionPoints(section.points.toString());
    setNewSectionRequired(section.is_required);
    setNewSectionAutoApprove(section.auto_approve);
    setShowAddSection(false);
  };

  /**
   * Save edited section
   */
  const handleSaveEditSection = () => {
    if (!editingSectionId) return;
    if (!newSectionTitle.trim()) {
      Alert.alert("Fout", "Vul een titel in voor de sectie");
      return;
    }

    setSections(sections.map((s) => {
      if (s.id === editingSectionId) {
        return {
          ...s,
          title: newSectionTitle.trim(),
          description: newSectionDescription.trim(),
          section_type: newSectionType,
          points: parseInt(newSectionPoints) || 0,
          is_required: newSectionRequired,
          auto_approve: newSectionAutoApprove,
        };
      }
      return s;
    }));

    // Reset form
    handleCancelEditSection();
  };

  /**
   * Cancel editing section
   */
  const handleCancelEditSection = () => {
    setEditingSectionId(null);
    setNewSectionTitle("");
    setNewSectionDescription("");
    setNewSectionType("checkbox");
    setNewSectionPoints("10");
    setNewSectionRequired(true);
    setNewSectionAutoApprove(false);
  };

  /**
   * Move section up
   */
  const handleMoveUp = (index: number) => {
    if (index === 0) return;
    const newSections = [...sections];
    [newSections[index - 1], newSections[index]] = [newSections[index], newSections[index - 1]];
    setSections(newSections);
  };

  /**
   * Move section down
   */
  const handleMoveDown = (index: number) => {
    if (index === sections.length - 1) return;
    const newSections = [...sections];
    [newSections[index], newSections[index + 1]] = [newSections[index + 1], newSections[index]];
    setSections(newSections);
  };

  const toggleRevealTarget = useCallback((target: string) => {
    setRevealTargets((prev) =>
      prev.includes(target)
        ? prev.filter((t) => t !== target)
        : [...prev, target]
    );
  }, []);

  /**
   * Create the opdracht
   */
  const handleCreate = async () => {
    if (!title.trim()) {
      Alert.alert("Fout", "Vul een titel in voor de opdracht");
      return;
    }

    if (sections.length === 0) {
      Alert.alert("Fout", "Voeg minimaal één sectie toe");
      return;
    }

    setIsSubmitting(true);

    try {
      const opdrachtConfig: OpdrachtConfig = {
        explainer: explainer.trim() || undefined,
        require_order: requireOrder,
        allow_partial_points: allowPartialPoints,
        review_mode: reviewMode,
        section_time_limits: false,
      };

      let challengeId: string;

      if (isEditMode && editChallenge) {
        // Update existing challenge
        const { error: updateError } = await supabase
          .from("challenges")
          .update({
            title: title.trim(),
            description: description.trim() || null,
            target_type: targetType,
            visibility: visibility,
            visible_to_team_id: visibility === "specific_team" ? selectedTeamId : null,
            points: totalPoints,
            config: opdrachtConfig,
            linked_event_id: linkedEventId || null,
            reveal_targets: linkedEventId ? revealTargets : [],
            updated_at: new Date().toISOString(),
          })
          .eq("id", editChallenge.id);

        if (updateError) throw updateError;
        challengeId = editChallenge.id;

        // Delete existing sections and recreate (simpler than diffing)
        await supabase
          .from("challenge_sections")
          .delete()
          .eq("challenge_id", challengeId);
      } else {
        // Create new challenge
        const { data: challengeData, error: challengeError } = await supabase
          .from("challenges")
          .insert({
            weekend_id: weekendId || null,
            title: title.trim(),
            description: description.trim() || null,
            challenge_type: "opdracht",
            target_type: targetType,
            visibility: visibility,
            visible_to_team_id: visibility === "specific_team" ? selectedTeamId : null,
            has_points: true,
            points: totalPoints,
            bonus_points: 0,
            status: "draft",
            config: opdrachtConfig,
            linked_event_id: linkedEventId || null,
            reveal_targets: linkedEventId ? revealTargets : [],
            created_by: activeProfile?.id,
          })
          .select()
          .single();

        if (challengeError) throw challengeError;
        challengeId = challengeData.id;
      }

      // Create sections (with auto_approve in config)
      const sectionInserts = sections.map((section, index) => ({
        challenge_id: challengeId,
        title: section.title,
        description: section.description || null,
        section_type: section.section_type,
        config: { auto_approve: section.auto_approve },
        order_index: index,
        is_required: section.is_required,
        points: section.points,
        time_limit_minutes: section.time_limit_minutes,
      }));

      const { error: sectionsError } = await supabase
        .from("challenge_sections")
        .insert(sectionInserts);

      if (sectionsError) throw sectionsError;

      // Success
      const message = isEditMode
        ? "Opdracht bijgewerkt!"
        : "Opdracht aangemaakt! Vergeet niet om de status naar 'Actief' te zetten.";
      Alert.alert("Succes", message, [
        {
          text: "OK",
          onPress: () => {
            resetForm();
            onSuccess();
            onClose();
          },
        },
      ]);
    } catch (err: any) {
      console.error("Error creating/updating opdracht:", err);
      Alert.alert("Fout", err.message || "Kon opdracht niet opslaan");
    } finally {
      setIsSubmitting(false);
    }
  };

  /**
   * Reset form
   */
  const resetForm = () => {
    setTitle("");
    setDescription("");
    setExplainer("");
    setTargetType("team");
    setVisibility("all");
    setSelectedTeamId(null);
    setRequireOrder(false);
    setAllowPartialPoints(true);
    setReviewMode("whole");
    setLinkedEventId(null);
    setRevealTargets([]);
    setSections([]);
    setStep("info");
    setEditingSectionId(null);
    setShowAddSection(false);
    setNewSectionTitle("");
    setNewSectionDescription("");
    setNewSectionType("checkbox");
    setNewSectionPoints("10");
    setNewSectionRequired(true);
    setNewSectionAutoApprove(false);
  };

  /**
   * Handle close
   */
  const handleClose = () => {
    if (title || sections.length > 0) {
      Alert.alert(
        "Weet je het zeker?",
        "Je wijzigingen worden niet opgeslagen.",
        [
          { text: "Annuleren", style: "cancel" },
          { text: "Ja, sluiten", onPress: () => { resetForm(); onClose(); } },
        ]
      );
    } else {
      onClose();
    }
  };

  /**
   * Render section type picker
   */
  const renderSectionTypePicker = () => (
    <View className="flex-row flex-wrap gap-2 mb-4">
      {(["checkbox", "photo", "text", "number", "location"] as SectionType[]).map((type) => (
        <TouchableOpacity
          key={type}
          onPress={() => setNewSectionType(type)}
          className="px-4 py-2 rounded-xl flex-row items-center"
          style={{
            backgroundColor: newSectionType === type ? SECTION_COLORS[type] + "30" : colors.surfaceSecondary,
            borderWidth: newSectionType === type ? 2 : 1,
            borderColor: newSectionType === type ? SECTION_COLORS[type] : colors.border,
          }}
        >
          <Ionicons
            name={SECTION_ICONS[type] as any}
            size={16}
            color={newSectionType === type ? SECTION_COLORS[type] : colors.textSecondary}
          />
          <Text
            className="ml-2 font-medium"
            style={{ color: newSectionType === type ? SECTION_COLORS[type] : colors.textSecondary }}
          >
            {SECTION_LABELS[type]}
          </Text>
        </TouchableOpacity>
      ))}
    </View>
  );

  return (
    <Modal
      visible={visible}
      animationType="slide"
      presentationStyle="pageSheet"
      onRequestClose={handleClose}
    >
      <SafeAreaView className="flex-1" style={{ backgroundColor: colors.background }} edges={["top", "bottom"]}>
        {/* Header */}
        <View
          className="px-4 py-4 flex-row items-center justify-between border-b"
          style={{ borderBottomColor: colors.border }}
        >
          <TouchableOpacity onPress={handleClose}>
            <Ionicons name="close" size={28} color={colors.text} />
          </TouchableOpacity>
          <Text className="text-xl font-bold" style={{ color: colors.text }}>
            {isEditMode ? "Opdracht bewerken" : "Nieuwe Opdracht"}
          </Text>
          <View style={{ width: 28 }} />
        </View>

        {/* Step Indicator */}
        <View className="flex-row px-4 py-3 gap-2">
          {["info", "sections", "settings"].map((s, i) => (
            <TouchableOpacity
              key={s}
              onPress={() => setStep(s as any)}
              className="flex-1 py-2 rounded-lg items-center"
              style={{
                backgroundColor: step === s ? colors.primary : colors.surfaceSecondary,
              }}
            >
              <Text
                className="font-medium"
                style={{ color: step === s ? "#FFFFFF" : colors.textSecondary }}
              >
                {i + 1}. {s === "info" ? "Info" : s === "sections" ? "Secties" : "Instellingen"}
              </Text>
            </TouchableOpacity>
          ))}
        </View>

        <ScrollView className="flex-1 px-4">
          {/* Step 1: Basic Info */}
          {step === "info" && (
            <View className="py-4">
              <Text className="text-sm font-medium mb-2" style={{ color: colors.textSecondary }}>
                Titel *
              </Text>
              <TextInput
                value={title}
                onChangeText={setTitle}
                placeholder="Bijv. Familie Speurtocht"
                placeholderTextColor={colors.textTertiary}
                className="p-4 rounded-xl text-base mb-4"
                style={{ backgroundColor: colors.surfaceSecondary, color: colors.text }}
              />

              <Text className="text-sm font-medium mb-2" style={{ color: colors.textSecondary }}>
                Korte beschrijving
              </Text>
              <TextInput
                value={description}
                onChangeText={setDescription}
                placeholder="Optionele korte beschrijving"
                placeholderTextColor={colors.textTertiary}
                className="p-4 rounded-xl text-base mb-4"
                style={{ backgroundColor: colors.surfaceSecondary, color: colors.text }}
                multiline
                numberOfLines={2}
              />

              <Text className="text-sm font-medium mb-2" style={{ color: colors.textSecondary }}>
                Uitleg (getoond bovenaan opdracht)
              </Text>
              <TextInput
                value={explainer}
                onChangeText={setExplainer}
                placeholder="Leg hier uit wat de opdracht inhoudt..."
                placeholderTextColor={colors.textTertiary}
                className="p-4 rounded-xl text-base mb-4"
                style={{ backgroundColor: colors.surfaceSecondary, color: colors.text }}
                multiline
                numberOfLines={4}
              />

              <Text className="text-sm font-medium mb-2" style={{ color: colors.textSecondary }}>
                Voor wie?
              </Text>
              <View className="flex-row gap-3 mb-4">
                <TouchableOpacity
                  onPress={() => setTargetType("team")}
                  className="flex-1 p-4 rounded-xl items-center"
                  style={{
                    backgroundColor: targetType === "team" ? colors.primary + "20" : colors.surfaceSecondary,
                    borderWidth: targetType === "team" ? 2 : 1,
                    borderColor: targetType === "team" ? colors.primary : colors.border,
                  }}
                >
                  <Ionicons
                    name="people"
                    size={24}
                    color={targetType === "team" ? colors.primary : colors.textSecondary}
                  />
                  <Text
                    className="mt-2 font-medium"
                    style={{ color: targetType === "team" ? colors.primary : colors.textSecondary }}
                  >
                    Team
                  </Text>
                </TouchableOpacity>

                <TouchableOpacity
                  onPress={() => setTargetType("individual")}
                  className="flex-1 p-4 rounded-xl items-center"
                  style={{
                    backgroundColor: targetType === "individual" ? colors.primary + "20" : colors.surfaceSecondary,
                    borderWidth: targetType === "individual" ? 2 : 1,
                    borderColor: targetType === "individual" ? colors.primary : colors.border,
                  }}
                >
                  <Ionicons
                    name="person"
                    size={24}
                    color={targetType === "individual" ? colors.primary : colors.textSecondary}
                  />
                  <Text
                    className="mt-2 font-medium"
                    style={{ color: targetType === "individual" ? colors.primary : colors.textSecondary }}
                  >
                    Individueel
                  </Text>
                </TouchableOpacity>
              </View>

              {/* Visibility */}
              <Text className="text-sm font-medium mb-2" style={{ color: colors.textSecondary }}>
                Zichtbaarheid
              </Text>
              <View className="flex-row flex-wrap mb-4">
                {[
                  { value: "all", label: "Alle teams", icon: "globe" },
                  { value: "specific_team", label: "Specifiek team", icon: "people-circle" },
                ].map((option) => (
                  <TouchableOpacity
                    key={option.value}
                    onPress={() => {
                      setVisibility(option.value as ChallengeVisibility);
                      if (option.value === "all") setSelectedTeamId(null);
                    }}
                    className="mr-2 mb-2 px-4 py-2 rounded-full flex-row items-center"
                    style={{
                      backgroundColor: visibility === option.value ? colors.primary : colors.surfaceSecondary,
                    }}
                  >
                    <Ionicons
                      name={option.icon as any}
                      size={16}
                      color={visibility === option.value ? "#FFFFFF" : colors.textSecondary}
                    />
                    <Text
                      className="ml-2 text-sm font-medium"
                      style={{ color: visibility === option.value ? "#FFFFFF" : colors.text }}
                    >
                      {option.label}
                    </Text>
                  </TouchableOpacity>
                ))}
              </View>

              {/* Team Selector (when specific team is selected) */}
              {visibility === "specific_team" && (
                <View className="mb-4">
                  <Text className="text-sm font-medium mb-2" style={{ color: colors.textSecondary }}>
                    Selecteer team
                  </Text>
                  {teamsLoading ? (
                    <View className="py-4 items-center">
                      <ActivityIndicator size="small" color={colors.primary} />
                    </View>
                  ) : teams.length === 0 ? (
                    <View
                      className="p-4 rounded-xl items-center"
                      style={{ backgroundColor: colors.surfaceSecondary }}
                    >
                      <Text style={{ color: colors.textSecondary }}>Geen teams beschikbaar</Text>
                    </View>
                  ) : (
                    <ScrollView horizontal showsHorizontalScrollIndicator={false}>
                      {teams.map((team) => (
                        <TouchableOpacity
                          key={team.id}
                          onPress={() => setSelectedTeamId(team.id)}
                          className="mr-2 px-4 py-3 rounded-xl flex-row items-center"
                          style={{
                            backgroundColor: selectedTeamId === team.id ? colors.primary : colors.surfaceSecondary,
                            borderWidth: selectedTeamId === team.id ? 0 : 1,
                            borderColor: colors.border,
                          }}
                        >
                          <View
                            className="w-8 h-8 rounded-full items-center justify-center mr-2"
                            style={{ backgroundColor: team.color || colors.primary }}
                          >
                            <Text className="font-bold text-white text-xs">
                              {team.name.substring(0, 2).toUpperCase()}
                            </Text>
                          </View>
                          <Text
                            className="font-medium"
                            style={{ color: selectedTeamId === team.id ? "#FFFFFF" : colors.text }}
                          >
                            {team.name}
                          </Text>
                        </TouchableOpacity>
                      ))}
                    </ScrollView>
                  )}
                </View>
              )}

              <TouchableOpacity
                onPress={() => setStep("sections")}
                className="py-4 rounded-xl items-center mt-4"
                style={{ backgroundColor: colors.primary }}
              >
                <Text className="font-bold text-white">Volgende: Secties toevoegen</Text>
              </TouchableOpacity>
            </View>
          )}

          {/* Step 2: Sections */}
          {step === "sections" && (
            <View className="py-4">
              {/* Sections List */}
              {sections.length > 0 && (
                <View className="mb-4">
                  <Text className="text-sm font-medium mb-3" style={{ color: colors.textSecondary }}>
                    Secties ({sections.length}) • Totaal: {totalPoints} punten
                  </Text>
                  
                  {sections.map((section, index) => (
                    <TouchableOpacity
                      key={section.id}
                      onPress={() => handleStartEditSection(section)}
                      activeOpacity={0.7}
                      className="rounded-xl p-4 mb-2 flex-row items-center"
                      style={{
                        backgroundColor: editingSectionId === section.id ? colors.primary + "10" : colors.surface,
                        borderWidth: editingSectionId === section.id ? 2 : 1,
                        borderColor: editingSectionId === section.id ? colors.primary : colors.border,
                      }}
                    >
                      <View
                        className="w-10 h-10 rounded-lg items-center justify-center mr-3"
                        style={{ backgroundColor: SECTION_COLORS[section.section_type] + "20" }}
                      >
                        <Ionicons
                          name={SECTION_ICONS[section.section_type] as any}
                          size={20}
                          color={SECTION_COLORS[section.section_type]}
                        />
                      </View>

                      <View className="flex-1">
                        <Text className="font-medium" style={{ color: colors.text }}>
                          {index + 1}. {section.title}
                        </Text>
                        <Text className="text-sm" style={{ color: colors.textSecondary }}>
                          {SECTION_LABELS[section.section_type]} • {section.points} punten
                          {!section.is_required && " • Optioneel"}
                          {section.auto_approve && " • Auto"}
                        </Text>
                      </View>

                      <View className="flex-row items-center gap-1">
                        <TouchableOpacity
                          onPress={(e) => { e.stopPropagation(); handleMoveUp(index); }}
                          disabled={index === 0}
                          className="p-2"
                          style={{ opacity: index === 0 ? 0.3 : 1 }}
                        >
                          <Ionicons name="chevron-up" size={20} color={colors.textSecondary} />
                        </TouchableOpacity>
                        <TouchableOpacity
                          onPress={(e) => { e.stopPropagation(); handleMoveDown(index); }}
                          disabled={index === sections.length - 1}
                          className="p-2"
                          style={{ opacity: index === sections.length - 1 ? 0.3 : 1 }}
                        >
                          <Ionicons name="chevron-down" size={20} color={colors.textSecondary} />
                        </TouchableOpacity>
                        <TouchableOpacity
                          onPress={(e) => { e.stopPropagation(); handleRemoveSection(section.id); }}
                          className="p-2"
                        >
                          <Ionicons name="trash-outline" size={20} color={colors.error} />
                        </TouchableOpacity>
                      </View>
                    </TouchableOpacity>
                  ))}
                </View>
              )}

              {/* Add/Edit Section Form */}
              {(showAddSection || editingSectionId) ? (
                <View
                  className="rounded-xl p-4 mb-4"
                  style={{ backgroundColor: colors.surface, borderWidth: 1, borderColor: colors.primary }}
                >
                  <Text className="font-bold mb-4" style={{ color: colors.text }}>
                    {editingSectionId ? "Sectie bewerken" : "Nieuwe sectie"}
                  </Text>

                  <Text className="text-sm font-medium mb-2" style={{ color: colors.textSecondary }}>
                    Type
                  </Text>
                  {renderSectionTypePicker()}

                  <Text className="text-sm font-medium mb-2" style={{ color: colors.textSecondary }}>
                    Titel *
                  </Text>
                  <TextInput
                    value={newSectionTitle}
                    onChangeText={setNewSectionTitle}
                    placeholder="Bijv. Maak een foto van..."
                    placeholderTextColor={colors.textTertiary}
                    className="p-4 rounded-xl text-base mb-4"
                    style={{ backgroundColor: colors.surfaceSecondary, color: colors.text }}
                  />

                  <Text className="text-sm font-medium mb-2" style={{ color: colors.textSecondary }}>
                    Beschrijving
                  </Text>
                  <TextInput
                    value={newSectionDescription}
                    onChangeText={setNewSectionDescription}
                    placeholder="Optionele uitleg..."
                    placeholderTextColor={colors.textTertiary}
                    className="p-4 rounded-xl text-base mb-4"
                    style={{ backgroundColor: colors.surfaceSecondary, color: colors.text }}
                    multiline
                  />

                  <View className="flex-row gap-4 mb-4">
                    <View className="flex-1">
                      <Text className="text-sm font-medium mb-2" style={{ color: colors.textSecondary }}>
                        Punten
                      </Text>
                      <TextInput
                        value={newSectionPoints}
                        onChangeText={setNewSectionPoints}
                        placeholder="10"
                        placeholderTextColor={colors.textTertiary}
                        className="p-4 rounded-xl text-base"
                        style={{ backgroundColor: colors.surfaceSecondary, color: colors.text }}
                        keyboardType="numeric"
                      />
                    </View>

                    <View className="flex-1">
                      <Text className="text-sm font-medium mb-2" style={{ color: colors.textSecondary }}>
                        Verplicht?
                      </Text>
                      <TouchableOpacity
                        onPress={() => setNewSectionRequired(!newSectionRequired)}
                        className="p-4 rounded-xl flex-row items-center justify-center"
                        style={{
                          backgroundColor: newSectionRequired ? colors.success + "20" : colors.surfaceSecondary,
                          borderWidth: 1,
                          borderColor: newSectionRequired ? colors.success : colors.border,
                        }}
                      >
                        <Ionicons
                          name={newSectionRequired ? "checkmark-circle" : "ellipse-outline"}
                          size={20}
                          color={newSectionRequired ? colors.success : colors.textSecondary}
                        />
                        <Text
                          className="ml-2 font-medium"
                          style={{ color: newSectionRequired ? colors.success : colors.textSecondary }}
                        >
                          {newSectionRequired ? "Ja" : "Nee"}
                        </Text>
                      </TouchableOpacity>
                    </View>
                  </View>

                  {/* Auto-approve toggle */}
                  <View className="mb-4">
                    <Text className="text-sm font-medium mb-2" style={{ color: colors.textSecondary }}>
                      Automatisch goedkeuren
                    </Text>
                    <TouchableOpacity
                      onPress={() => setNewSectionAutoApprove(!newSectionAutoApprove)}
                      className="p-4 rounded-xl flex-row items-center justify-between"
                      style={{
                        backgroundColor: newSectionAutoApprove ? "#3B82F620" : colors.surfaceSecondary,
                        borderWidth: 1,
                        borderColor: newSectionAutoApprove ? "#3B82F6" : colors.border,
                      }}
                    >
                      <View className="flex-row items-center">
                        <Ionicons
                          name="flash"
                          size={20}
                          color={newSectionAutoApprove ? "#3B82F6" : colors.textSecondary}
                        />
                        <Text
                          className="ml-2"
                          style={{ color: newSectionAutoApprove ? "#3B82F6" : colors.textSecondary }}
                        >
                          {newSectionAutoApprove ? "Inzendingen worden direct goedgekeurd" : "Inzendingen moeten beoordeeld worden"}
                        </Text>
                      </View>
                      <Ionicons
                        name={newSectionAutoApprove ? "checkmark-circle" : "ellipse-outline"}
                        size={24}
                        color={newSectionAutoApprove ? "#3B82F6" : colors.textTertiary}
                      />
                    </TouchableOpacity>
                  </View>

                  <View className="flex-row gap-3">
                    <TouchableOpacity
                      onPress={() => {
                        if (editingSectionId) {
                          handleCancelEditSection();
                        } else {
                          setShowAddSection(false);
                        }
                      }}
                      className="flex-1 py-3 rounded-xl items-center"
                      style={{ backgroundColor: colors.surfaceSecondary }}
                    >
                      <Text style={{ color: colors.textSecondary }}>Annuleren</Text>
                    </TouchableOpacity>
                    <TouchableOpacity
                      onPress={editingSectionId ? handleSaveEditSection : handleAddSection}
                      className="flex-1 py-3 rounded-xl items-center"
                      style={{ backgroundColor: colors.primary }}
                    >
                      <Text className="font-semibold text-white">
                        {editingSectionId ? "Opslaan" : "Toevoegen"}
                      </Text>
                    </TouchableOpacity>
                  </View>
                </View>
              ) : (
                <TouchableOpacity
                  onPress={() => setShowAddSection(true)}
                  className="py-4 rounded-xl items-center flex-row justify-center mb-4"
                  style={{ backgroundColor: colors.surfaceSecondary, borderWidth: 2, borderStyle: "dashed", borderColor: colors.border }}
                >
                  <Ionicons name="add" size={24} color={colors.primary} />
                  <Text className="ml-2 font-semibold" style={{ color: colors.primary }}>
                    Sectie toevoegen
                  </Text>
                </TouchableOpacity>
              )}

              {sections.length > 0 && (
                <TouchableOpacity
                  onPress={() => setStep("settings")}
                  className="py-4 rounded-xl items-center mt-4"
                  style={{ backgroundColor: colors.primary }}
                >
                  <Text className="font-bold text-white">Volgende: Instellingen</Text>
                </TouchableOpacity>
              )}
            </View>
          )}

          {/* Step 3: Settings */}
          {step === "settings" && (
            <View className="py-4">
              <View
                className="rounded-xl p-4 mb-4"
                style={{ backgroundColor: colors.surface, borderWidth: 1, borderColor: colors.border }}
              >
                <View className="flex-row items-center justify-between mb-4">
                  <View className="flex-1 mr-4">
                    <Text className="font-medium" style={{ color: colors.text }}>
                      Volgorde verplicht
                    </Text>
                    <Text className="text-sm" style={{ color: colors.textSecondary }}>
                      Secties moeten in volgorde worden afgerond
                    </Text>
                  </View>
                  <Switch
                    value={requireOrder}
                    onValueChange={setRequireOrder}
                    trackColor={{ false: colors.border, true: colors.primary + "60" }}
                    thumbColor={requireOrder ? colors.primary : colors.textTertiary}
                  />
                </View>

                <View className="flex-row items-center justify-between mb-4">
                  <View className="flex-1 mr-4">
                    <Text className="font-medium" style={{ color: colors.text }}>
                      Deelpunten toestaan
                    </Text>
                    <Text className="text-sm" style={{ color: colors.textSecondary }}>
                      Punten verdienen voor deels voltooide opdracht
                    </Text>
                  </View>
                  <Switch
                    value={allowPartialPoints}
                    onValueChange={setAllowPartialPoints}
                    trackColor={{ false: colors.border, true: colors.primary + "60" }}
                    thumbColor={allowPartialPoints ? colors.primary : colors.textTertiary}
                  />
                </View>

                <View>
                  <Text className="font-medium mb-2" style={{ color: colors.text }}>
                    Beoordeling
                  </Text>
                  <View className="flex-row gap-3">
                    <TouchableOpacity
                      onPress={() => setReviewMode("whole")}
                      className="flex-1 p-3 rounded-xl items-center"
                      style={{
                        backgroundColor: reviewMode === "whole" ? colors.primary + "20" : colors.surfaceSecondary,
                        borderWidth: reviewMode === "whole" ? 2 : 1,
                        borderColor: reviewMode === "whole" ? colors.primary : colors.border,
                      }}
                    >
                      <Text
                        className="font-medium"
                        style={{ color: reviewMode === "whole" ? colors.primary : colors.textSecondary }}
                      >
                        Hele opdracht
                      </Text>
                    </TouchableOpacity>
                    <TouchableOpacity
                      onPress={() => setReviewMode("per_section")}
                      className="flex-1 p-3 rounded-xl items-center"
                      style={{
                        backgroundColor: reviewMode === "per_section" ? colors.primary + "20" : colors.surfaceSecondary,
                        borderWidth: reviewMode === "per_section" ? 2 : 1,
                        borderColor: reviewMode === "per_section" ? colors.primary : colors.border,
                      }}
                    >
                      <Text
                        className="font-medium"
                        style={{ color: reviewMode === "per_section" ? colors.primary : colors.textSecondary }}
                      >
                        Per sectie
                      </Text>
                    </TouchableOpacity>
                  </View>
                </View>
              </View>

              {/* Event Linking */}
              <View className="mb-4">
                <Text className="text-sm font-medium mb-2" style={{ color: colors.textSecondary }}>
                  Koppelen aan evenement (optioneel)
                </Text>
                <Text className="text-xs mb-3" style={{ color: colors.textTertiary }}>
                  Bij goedkeuring wordt het gekoppelde evenement onthuld voor het team.
                </Text>

                {eventsLoading ? (
                  <View className="py-4 items-center">
                    <ActivityIndicator size="small" color={colors.primary} />
                  </View>
                ) : events.length === 0 ? (
                  <View
                    className="p-4 rounded-xl items-center"
                    style={{ backgroundColor: colors.surfaceSecondary }}
                  >
                    <Text style={{ color: colors.textSecondary }}>Geen evenementen beschikbaar</Text>
                  </View>
                ) : (
                  <>
                    <ScrollView horizontal showsHorizontalScrollIndicator={false} className="mb-3">
                      <TouchableOpacity
                        onPress={() => {
                          setLinkedEventId(null);
                          setRevealTargets([]);
                        }}
                        className="mr-2 px-4 py-3 rounded-xl"
                        style={{
                          backgroundColor: !linkedEventId ? colors.primary : colors.surfaceSecondary,
                          borderWidth: !linkedEventId ? 0 : 1,
                          borderColor: colors.border,
                        }}
                      >
                        <Text
                          className="font-medium"
                          style={{ color: !linkedEventId ? "#FFFFFF" : colors.text }}
                        >
                          Geen koppeling
                        </Text>
                      </TouchableOpacity>
                      {events.map((event) => (
                        <TouchableOpacity
                          key={event.id}
                          onPress={() => setLinkedEventId(event.id)}
                          className="mr-2 px-4 py-3 rounded-xl"
                          style={{
                            backgroundColor: linkedEventId === event.id ? colors.primary : colors.surfaceSecondary,
                            borderWidth: linkedEventId === event.id ? 0 : 1,
                            borderColor: colors.border,
                          }}
                        >
                          <Text
                            className="font-medium"
                            style={{ color: linkedEventId === event.id ? "#FFFFFF" : colors.text }}
                            numberOfLines={1}
                          >
                            {event.title}
                          </Text>
                          <Text
                            className="text-xs mt-0.5"
                            style={{ color: linkedEventId === event.id ? "rgba(255,255,255,0.7)" : colors.textTertiary }}
                          >
                            {format(new Date(event.starts_at), "d MMM", { locale: nl })}
                            {event.is_revealed ? "" : " • 🔒"}
                          </Text>
                        </TouchableOpacity>
                      ))}
                    </ScrollView>

                    {linkedEventId && (
                      <View
                        className="p-4 rounded-xl"
                        style={{ backgroundColor: colors.surfaceSecondary }}
                      >
                        <Text className="font-medium mb-3" style={{ color: colors.text }}>
                          Wat moet onthuld worden?
                        </Text>
                        <View className="flex-row flex-wrap">
                          {[
                            { value: "title", label: "Titel", icon: "text" },
                            { value: "location", label: "Locatie", icon: "location" },
                            { value: "description", label: "Beschrijving", icon: "document-text" },
                          ].map((target) => (
                            <TouchableOpacity
                              key={target.value}
                              onPress={() => toggleRevealTarget(target.value)}
                              className="mr-2 mb-2 px-3 py-2 rounded-lg flex-row items-center"
                              style={{
                                backgroundColor: revealTargets.includes(target.value)
                                  ? colors.primary
                                  : colors.surface,
                                borderWidth: 1,
                                borderColor: revealTargets.includes(target.value)
                                  ? colors.primary
                                  : colors.border,
                              }}
                            >
                              <Ionicons
                                name={target.icon as any}
                                size={16}
                                color={revealTargets.includes(target.value) ? "#FFFFFF" : colors.textSecondary}
                              />
                              <Text
                                className="ml-2 text-sm font-medium"
                                style={{
                                  color: revealTargets.includes(target.value) ? "#FFFFFF" : colors.text,
                                }}
                              >
                                {target.label}
                              </Text>
                            </TouchableOpacity>
                          ))}
                        </View>
                      </View>
                    )}
                  </>
                )}
              </View>

              {/* Summary */}
              <View
                className="rounded-xl p-4 mb-4"
                style={{ backgroundColor: colors.primary + "10", borderWidth: 1, borderColor: colors.primary + "30" }}
              >
                <Text className="font-bold mb-2" style={{ color: colors.text }}>
                  Samenvatting
                </Text>
                <Text style={{ color: colors.textSecondary }}>
                  • {sections.length} secties
                </Text>
                <Text style={{ color: colors.textSecondary }}>
                  • {totalPoints} punten totaal
                </Text>
                <Text style={{ color: colors.textSecondary }}>
                  • Voor: {targetType === "team" ? "Teams" : "Individuen"}
                </Text>
                <Text style={{ color: colors.textSecondary }}>
                  • Status: Concept (activeer na aanmaken)
                </Text>
                {linkedEventId && (
                  <Text style={{ color: colors.textSecondary }}>
                    • Gekoppeld aan evenement ({revealTargets.join(", ") || "geen onthulling"})
                  </Text>
                )}
              </View>

              <TouchableOpacity
                onPress={handleCreate}
                disabled={isSubmitting}
                className="py-4 rounded-xl items-center flex-row justify-center"
                style={{ backgroundColor: colors.success }}
              >
                {isSubmitting ? (
                  <ActivityIndicator color="#FFFFFF" />
                ) : (
                  <>
                    <Ionicons name="checkmark" size={24} color="#FFFFFF" />
                    <Text className="font-bold text-white ml-2">Opdracht aanmaken</Text>
                  </>
                )}
              </TouchableOpacity>
            </View>
          )}

          <View className="h-8" />
        </ScrollView>
      </SafeAreaView>
    </Modal>
  );
};

export default CreateOpdrachtModal;
